import {
  useCreateGeneration,
  useCreateImage,
} from '@hooks/apiHooks/generationsHooks.ts';
import {
  useSaveMyCreation,
  useUpdateMyCreation,
} from '@hooks/apiHooks/myCreationsHooks';
import {
  autoSaveImage,
  autoSaveSong,
} from '@hooks/generation/generationAutoSave';
import { useAvatarMessaging } from '@hooks/generation/useAvatarMessaging';
import { useBusinessPlan } from '@hooks/generation/useBusinessPlan';
import { useDesignerImages } from '@hooks/generation/useDesignerImages';
import { useDualSongChecking } from '@hooks/generation/useDualSongChecking';
import { useGenerationMessageBridge } from '@hooks/generation/useGenerationMessageBridge';
import { useGenerationProduce } from '@hooks/generation/useGenerationProduce';
import { usePresentation } from '@hooks/generation/usePresentation';
import { useRefSync } from '@hooks/generation/useRefSync';
import { useStationAutoActions } from '@hooks/generation/useStationAutoActions';
import { useWebpage } from '@hooks/generation/useWebpage';
import { useWorkflowCards } from '@hooks/generation/useWorkflowCards';
import { useStationMode } from '@hooks/station';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useGenerationState } from '@hooks/storeHooks/useGenerationState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { setVolume } from '@store/reducers/musicStore.ts';
import { useQueryClient } from '@tanstack/react-query';
import {
  sendStopTalkingMessage,
  setBusinessPlanTaskId,
  setPresentationTaskId,
  setRegenerationFunctions,
  setWebpageTaskId,
} from '@utils/station';
import {
  applyProduceErrorModalFromApiError,
  type ProduceErrorModalSetters,
} from '@utils/applyProduceErrorModalFromApiError';
import { isStreamUrl } from '@utils/streamUtils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ProduceErrorModal } from '../../../components/Modals/ProduceErrorModal';

import { getFanCompanions } from '../../../config.ts';
import {
  MUSIC_ALBUM_COVER_REGENERATION,
  MUSIC_LYRICS_REGENERATION,
  MUSIC_SONG_TITLE_REGENERATION,
  REGENERATE_CREATION,
} from '../../../features/AvatarSession/constants/musicGenerationRealtimeTools';
import { DISCONNECT_AVATAR_PRESERVE_GENERATION_PAGE } from '../../../features/AvatarSession/constants/sessionEvents';
import {
  type DataChannelMessage,
  useDataChannel,
} from '../../../features/AvatarSession/hooks/useDataChannel';
import { useRegenerateCreation } from '../../../features/AvatarSession/hooks/useRegenerateCreation';
import {
  GenerationResponse,
  ImageGenerationRequest,
  updateSavedCreationId,
} from '../../../services/generationsApi';
import { OidcUser } from '../../../types/oidcUser';
import { analytics } from '../../../utils/analytics';
import { buildGenerationPlaylist } from '../../../utils/buildGenerationPlaylist';
import { StationBusinessPlanView } from './station/StationBusinessPlanView';
import { StationDesignerUI } from './station/StationDesignerUI';
import { StationPresentationView } from './station/StationPresentationView';
import { StationWebpageView } from './station/StationWebpageView';
import { StationMusicGenerationUI } from './StationMusicGenerationUI';
import { WorkflowVisualization } from './WorkflowVisualization';

interface GenerationProps {
  musicGeneration?: GenerationResponse;
  userInput?: string;
  fanSlug: string;
  isPresentationSession?: boolean;
  isBusinessPlanSession?: boolean;
  isWebpageSession?: boolean;
  taskId?: string;
}

export const Generation: React.FC<GenerationProps> = ({
  musicGeneration,
  userInput,
  fanSlug,
  isPresentationSession = false,
  isBusinessPlanSession = false,
  isWebpageSession = false,
  taskId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isStationMode = useStationMode();
  const demoFans = useMemo(
    () => getFanCompanions(isStationMode),
    [isStationMode],
  );
  const {
    activePlaylist,
    setActivePlaylist,
    volume,
    musicPlaying,
    setMusicPlaying,
  } = useMusicState();
  const { setActivePopup, setIsMinimized } = useCommonState();
  const { sessionId } = useSessionFeatureState();
  const { user } = useAuth();

  const dataChannelResult = useDataChannel();
  const {
    sendMessage,
    onMessage,
    isOpen: isDataChannelOpen,
  } = isStationMode
    ? {
        sendMessage: () => {},
        onMessage: () => () => {},
        isOpen: false,
      }
    : dataChannelResult;
  const {
    selectedImageUrl,
    selectedImageId,
    setSelectedImageUrl,
    setSelectedImageId,
    regeneratedImageUrl,
    setRegeneratedImageUrl,
    setCurrentMusicContext,
    currentMusicPrompt,
    currentMusicStyle,
    currentMusicThumbnailUrl,
    selectedSongId,
    setSelectedSongId,
    isRegeneratingImage,
    isRegeneratingMusic,
    setIsRegeneratingImage,
    setIsRegeneratingMusic,
    musicCoverOverrides,
    setMusicCoverOverride,
    resetMusicCoverOverrides,
    setRegeneratedTaskId,
    isRegeneratingLyrics,
    setIsRegeneratingLyrics,
  } = useGenerationState();

  const sendMessageWithBridge = useGenerationMessageBridge(sendMessage);

  const [isMuted, setIsMuted] = useState(false);

  const [editedLyrics, setEditedLyrics] = useState<string | null>(null);
  const [editedTitles, setEditedTitles] = useState<
    Partial<Record<1 | 2, string>>
  >({});

  const fanData = useMemo(
    () => demoFans.find((fan) => fan.slug === fanSlug),
    [fanSlug],
  );

  const isDesignerSession =
    !!userInput &&
    !isPresentationSession &&
    !isBusinessPlanSession &&
    !isWebpageSession;
  const isPresentationMode = isPresentationSession && !!userInput;
  const isBusinessPlanMode = isBusinessPlanSession && !!userInput;
  const isWebpageMode = isWebpageSession && !!userInput;
  const activeMusicGeneration = musicGeneration;
  const isDesignerMode = isDesignerSession;
  const isMusicGenerationSession = !!activeMusicGeneration?.taskId;
  const { mutateAsync: createImageRequest } = useCreateImage();

  // Station runs are curated, so the API skips moderation for their images too
  const createImageMutation = useCallback(
    (args: ImageGenerationRequest) =>
      createImageRequest({ ...args, stationMode: isStationMode }),
    [createImageRequest, isStationMode],
  );
  const { mutateAsync: createMusicGenerationMutation } = useCreateGeneration();

  const createMusicGenerationForFan = useCallback(
    (args: {
      prompt: string;
      musicStyle?: string;
      thumbnailUrl?: string;
      userPhotoUrl?: string;
      uniqueId: string;
    }) => {
      if (!fanData) {
        return createMusicGenerationMutation({
          ...args,
          stationMode: isStationMode,
          //numOutputs: 1,
        });
      }
      return createMusicGenerationMutation({
        ...args,
        language: fanData.language ?? 'English',
        gender: fanData.gender,
        thumbnailUrl: args.thumbnailUrl || fanData.full_image_url,
        userPhotoUrl: args.userPhotoUrl,
        voiceId: fanData.voice_id,
        artistTag:
          fanData.artist_alias ||
          fanData.alias ||
          fanData.alias_tag ||
          undefined,
        stationMode: isStationMode,
        // numOutputs: Features.enableSingleSongMode ? 1 : undefined,
      });
    },
    [createMusicGenerationMutation, fanData, isStationMode],
  );

  const selectedImageUrlRef = useRefSync(selectedImageUrl);
  const selectedImageIdRef = useRefSync(selectedImageId);
  const currentMusicPromptRef = useRefSync(currentMusicPrompt);
  const currentMusicStyleRef = useRefSync(currentMusicStyle);
  const currentMusicThumbnailUrlRef = useRefSync(currentMusicThumbnailUrl);
  const selectedSongIdRef = useRefSync(selectedSongId);
  const musicGenerationRef = useRefSync(musicGeneration);

  const resolveMusicCardImage = useCallback(
    (songId: 1 | 2) => {
      const override = musicCoverOverrides[songId];
      if (override) {
        return override;
      }

      if (!activeMusicGeneration) {
        return currentMusicThumbnailUrlRef.current ?? null;
      }

      if (songId === 1) {
        return (
          activeMusicGeneration.thumbnailUrl1 ||
          activeMusicGeneration.musicGptCoverUrl ||
          currentMusicThumbnailUrlRef.current ||
          null
        );
      }

      return (
        activeMusicGeneration.thumbnailUrl2 ||
        activeMusicGeneration.musicGptCoverUrl ||
        currentMusicThumbnailUrlRef.current ||
        null
      );
    },
    [activeMusicGeneration, musicCoverOverrides],
  );

  const {
    designImage1,
    designImage2,
    isLoadingDesignImages,
    setDesignImage1,
    setDesignImage2,
  } = useDesignerImages({
    userInput: isDesignerSession ? userInput : undefined,
    createImageMutation,
  });

  const {
    presentationUrl,
    isLoadingPresentation,
    taskId: presentationTaskId,
  } = usePresentation({
    taskId: isPresentationMode && taskId ? taskId : null,
  });

  const {
    businessPlanUrl,
    isLoadingBusinessPlan,
    taskId: businessPlanTaskId,
  } = useBusinessPlan({
    taskId: isBusinessPlanMode && taskId ? taskId : null,
  });

  const {
    webpageUrl,
    isLoadingWebpage,
    taskId: webpageTaskIdValue,
  } = useWebpage({
    taskId: isWebpageMode && taskId ? taskId : null,
  });

  // Save creation hook
  const { mutate: saveCreation, mutateAsync: saveCreationAsync } =
    useSaveMyCreation();
  const { mutate: updateCreation } = useUpdateMyCreation();

  const handleGenerationTitleChange = useCallback(
    (_activeCardVersion: 1 | 2, value: string) => {
      // One shared title for both outputs — previously only the selected card's slot updated,
      // so auto-save for the other version kept the API title and My Creations looked split.
      setEditedTitles((prev) => ({ ...prev, 1: value, 2: value }));

      const trimmed = value.trim();
      if (savedSongCreationId1.current) {
        updateCreation({
          id: savedSongCreationId1.current,
          data: { data: { title: trimmed } },
        });
      }
      if (savedSongCreationId2.current) {
        updateCreation({
          id: savedSongCreationId2.current,
          data: { data: { title: trimmed } },
        });
      }
    },
    [updateCreation],
  );

  const hasSavedImage1 = useRef(false);
  const hasSavedImage2 = useRef(false);
  const hasSavedSong1 = useRef(false);
  const hasSavedSong2 = useRef(false);
  const produceErrorModalSettersRef = useRef<ProduceErrorModalSetters | null>(
    null,
  );

  // Track creation IDs for updates
  const savedSongCreationId1 = useRef<string | null>(null);
  const savedSongCreationId2 = useRef<string | null>(null);
  const savedImageCreationId1 = useRef<string | null>(null);
  const savedImageCreationId2 = useRef<string | null>(null);

  const lastPatchedCover1 = useRef<string | null>(null);
  const lastPatchedCover2 = useRef<string | null>(null);

  const [song1SavedId, setSong1SavedId] = useState<string | null>(null);
  const [song2SavedId, setSong2SavedId] = useState<string | null>(null);
  const [image1SavedId, setImage1SavedId] = useState<string | null>(null);
  const [image2SavedId, setImage2SavedId] = useState<string | null>(null);

  // Update song thumbnail when image is regenerated
  const updateSongThumbnail = useCallback(
    (songVersion: 1 | 2, thumbnailUrl: string) => {
      const songCreationIdRef =
        songVersion === 1 ? savedSongCreationId1 : savedSongCreationId2;

      // Only update if we have a saved song creation ID
      if (songCreationIdRef.current) {
        console.log(
          `Calling updateCreation mutation for song ${songVersion} with ID: ${songCreationIdRef.current}`,
        );
        updateCreation(
          {
            id: songCreationIdRef.current,
            data: {
              data: {
                albumThumbnail: thumbnailUrl,
                previewImage: thumbnailUrl,
              },
            },
          },
          {
            onSuccess: (response) => {
              console.log(
                `✅ Song ${songVersion} thumbnail updated successfully:`,
                response,
              );
            },
            onError: (error) => {
              console.error(
                `❌ Error updating song ${songVersion} thumbnail:`,
                error,
              );
            },
          },
        );
      } else {
        console.log(
          `⚠️ No saved song creation ID for version ${songVersion}, skipping thumbnail update`,
        );
      }
    },
    [updateCreation],
  );

  const previousTaskIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentTaskId = taskId || activeMusicGeneration?.taskId;
    if (currentTaskId === previousTaskIdRef.current) {
      return;
    }
    previousTaskIdRef.current = currentTaskId;
    resetMusicCoverOverrides();
    hasAutoPlayedSong1Ref.current = false;
    setSong1SavedId(null);
    setSong2SavedId(null);
  }, [taskId, activeMusicGeneration?.taskId, resetMusicCoverOverrides]);

  const previousLyricsRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentLyrics = activeMusicGeneration?.lyrics;

    if (
      isRegeneratingLyrics &&
      currentLyrics &&
      previousLyricsRef.current !== undefined &&
      previousLyricsRef.current !== currentLyrics
    ) {
      setIsRegeneratingLyrics(false);
    }

    previousLyricsRef.current = currentLyrics;
  }, [
    activeMusicGeneration?.lyrics,
    isRegeneratingLyrics,
    setIsRegeneratingLyrics,
  ]);

  const { handleRegenerateCreation } = useRegenerateCreation({
    isDesignerSession,
    isMusicGenerationSession,
    taskId: activeMusicGeneration?.taskId ?? null,
    selectedImageUrlRef,
    selectedImageIdRef,
    createImageMutation,
    setIsRegeneratingImage,
    setRegeneratedImageUrl,
    sendMessage,
    setIsRegeneratingMusic,
    selectedSongIdRef,
    resolveMusicCardImage,
    setMusicCoverOverride,
    currentMusicPromptRef,
    currentMusicStyleRef,
    currentMusicThumbnailUrlRef,
    setCurrentMusicContext,
    musicGenerationRef,
    createMusicGeneration: createMusicGenerationForFan,
    setRegeneratedTaskId,
    setIsRegeneratingLyrics,
    updateSongThumbnail,
    savedSongCreationId1Ref: savedSongCreationId1,
    savedSongCreationId2Ref: savedSongCreationId2,
    onRegenerationApiError: (error: unknown) => {
      const setters = produceErrorModalSettersRef.current;
      if (setters) {
        applyProduceErrorModalFromApiError(error, setters);
      }
    },
  });

  // Handle regeneration requests from avatar (skip in Station mode)
  useEffect(() => {
    if (isStationMode) {
      return;
    }

    const handleFunctionMessage = (message: DataChannelMessage) => {
      if (message.event !== 'function_implicitly_called') {
        return;
      }

      const payload = (message?.data ?? {}) as Record<string, any>;
      const callId = payload.call_id as string | undefined;
      const functionName = payload.name as string | undefined;
      const modificationRequest = payload.arguments?.modification_request as
        | string
        | undefined;
      const requestType = payload.arguments?.type as
        | 'lyrics'
        | 'title'
        | 'cover'
        | 'music'
        | 'image'
        | undefined;

      const isMusicLyricsFn = functionName === MUSIC_LYRICS_REGENERATION;
      const isMusicCoverFn = functionName === MUSIC_ALBUM_COVER_REGENERATION;
      const isMusicTitleFn = functionName === MUSIC_SONG_TITLE_REGENERATION;
      const isDesignerRegen = functionName === REGENERATE_CREATION;

      if (
        !callId ||
        (!isMusicLyricsFn &&
          !isMusicCoverFn &&
          !isMusicTitleFn &&
          !isDesignerRegen)
      ) {
        return;
      }

      const sendFunctionOutput = (content: string) => {
        sendMessage({
          type: 'send_function_output',
          data: {
            call_id: callId,
            output: { content },
          },
        });
      };

      if (!modificationRequest) {
        sendFunctionOutput('Continue to support the user.');
        return;
      }

      let regenType: 'lyrics' | 'title' | 'cover' | 'music' | 'image';
      if (isMusicLyricsFn) {
        if (!isMusicGenerationSession) {
          sendFunctionOutput('Continue to support the user.');
          return;
        }
        regenType = 'lyrics';
      } else if (isMusicCoverFn) {
        if (!isMusicGenerationSession) {
          sendFunctionOutput('Continue to support the user.');
          return;
        }
        regenType = 'cover';
      } else if (isMusicTitleFn) {
        if (!isMusicGenerationSession) {
          sendFunctionOutput('Continue to support the user.');
          return;
        }
        regenType = 'title';
      } else {
        regenType =
          requestType ??
          (isDesignerSession ? 'image' : ('music' as 'music' | 'image'));
        if (!isDesignerSession && !requestType) {
          sendFunctionOutput('Continue to support the user.');
          return;
        }
      }

      if (regenType === 'image' || regenType === 'cover') {
        const songNumber = selectedSongIdRef.current || 1;
        analytics.logEvent('music_generation_regenerate_artwork', {
          change_source: 'voice',
          song_number: songNumber,
        });
      }

      void handleRegenerateCreation(
        {
          type: regenType,
          modification_request: modificationRequest,
        },
        sendFunctionOutput,
      );
    };

    const unsubscribe = onMessage(handleFunctionMessage);
    return () => {
      unsubscribe?.();
    };
  }, [
    handleRegenerateCreation,
    isDesignerSession,
    isMusicGenerationSession,
    onMessage,
    sendMessage,
    isStationMode,
  ]);

  // Register regeneration functions for Station mode window functions
  useEffect(() => {
    if (isStationMode && handleRegenerateCreation && setSelectedSongId) {
      setRegenerationFunctions(
        handleRegenerateCreation,
        setSelectedSongId,
        () => selectedSongId,
      );
    }

    return () => {
      if (isStationMode) {
        setRegenerationFunctions(null as any, null as any, null as any);
      }
    };
  }, [
    isStationMode,
    handleRegenerateCreation,
    setSelectedSongId,
    selectedSongId,
  ]);

  // Store presentation taskId for regeneration
  useEffect(() => {
    if (isPresentationMode && presentationTaskId) {
      setPresentationTaskId(presentationTaskId);
    }

    return () => {
      if (isPresentationMode) {
        setPresentationTaskId(null);
      }
    };
  }, [isPresentationMode, presentationTaskId]);

  // Store business plan taskId for regeneration
  useEffect(() => {
    if (isBusinessPlanMode && businessPlanTaskId) {
      setBusinessPlanTaskId(businessPlanTaskId);
    }

    return () => {
      if (isBusinessPlanMode) {
        setBusinessPlanTaskId(null);
      }
    };
  }, [isBusinessPlanMode, businessPlanTaskId]);

  // Store webpage taskId for regeneration
  useEffect(() => {
    if (isWebpageMode && webpageTaskIdValue) {
      setWebpageTaskId(webpageTaskIdValue);
    }

    return () => {
      if (isWebpageMode) {
        setWebpageTaskId(null);
      }
    };
  }, [isWebpageMode, webpageTaskIdValue]);

  const hasEverHadSong1Ref = useRef(false);
  const hasEverHadSong2Ref = useRef(false);
  const previousTaskIdForRefsRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentTaskId = taskId || activeMusicGeneration?.taskId;
    // Only reset refs if taskId actually changed
    if (currentTaskId === previousTaskIdForRefsRef.current) {
      return;
    }
    previousTaskIdForRefsRef.current = currentTaskId;
    hasEverHadSong1Ref.current = false;
    hasEverHadSong2Ref.current = false;
  }, [taskId, activeMusicGeneration?.taskId]);

  useEffect(() => {
    if (activeMusicGeneration?.songUrl1) {
      hasEverHadSong1Ref.current = true;
    }
    if (activeMusicGeneration?.songUrl2) {
      hasEverHadSong2Ref.current = true;
    }
  }, [activeMusicGeneration?.songUrl1, activeMusicGeneration?.songUrl2]);

  const queryClient = useQueryClient();

  const handleSongReady = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (songVersion: 1 | 2, url: string, _source: 'polling' | 'stream') => {
      if (!activeMusicGeneration) return;

      if (songVersion === 1) {
        hasEverHadSong1Ref.current = true;
      } else {
        hasEverHadSong2Ref.current = true;
      }

      queryClient.setQueryData<GenerationResponse>(
        ['generationStatus', activeMusicGeneration.taskId],
        (oldData) => {
          if (!oldData) return oldData;
          const currentUrl = oldData[
            `songUrl${songVersion}` as keyof GenerationResponse
          ] as string | undefined;

          if (!currentUrl) {
            return {
              ...oldData,
              [`songUrl${songVersion}`]: url,
            };
          }

          return oldData;
        },
      );
    },
    [activeMusicGeneration, queryClient],
  );

  const hasSong1 =
    !!activeMusicGeneration?.songUrl1 || hasEverHadSong1Ref.current;
  const hasSong2 =
    !!activeMusicGeneration?.songUrl2 || hasEverHadSong2Ref.current;

  const song1Ready = hasSong1;
  const song2Ready = hasSong2;

  const hasLyrics = !!activeMusicGeneration?.lyrics;
  const songsAreReady = song1Ready && song2Ready;

  useEffect(() => {
    if (!activeMusicGeneration?.taskId) return;

    const queryKey = ['generationStatus', activeMusicGeneration.taskId];

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event?.type === 'updated' &&
        Array.isArray(event.query.queryKey) &&
        event.query.queryKey[0] === 'generationStatus' &&
        event.query.queryKey[1] === activeMusicGeneration.taskId
      ) {
        const cachedData =
          queryClient.getQueryData<GenerationResponse>(queryKey);
        const serverData = event.query.state.data as
          | GenerationResponse
          | undefined;

        if (cachedData && serverData) {
          const cachedUrl1 = cachedData.songUrl1;
          const cachedUrl2 = cachedData.songUrl2;
          const serverUrl1 = serverData.songUrl1;
          const serverUrl2 = serverData.songUrl2;

          if ((cachedUrl1 && !serverUrl1) || (cachedUrl2 && !serverUrl2)) {
            queryClient.setQueryData<GenerationResponse>(
              queryKey,
              (oldData) => {
                if (!oldData) return oldData;
                return {
                  ...oldData,
                  ...(cachedUrl1 && !serverUrl1 && { songUrl1: cachedUrl1 }),
                  ...(cachedUrl2 && !serverUrl2 && { songUrl2: cachedUrl2 }),
                };
              },
            );
          }
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [activeMusicGeneration?.taskId, queryClient]);

  useDualSongChecking({
    taskId: activeMusicGeneration?.taskId || null,
    enabled:
      isStationMode &&
      isMusicGenerationSession &&
      !!activeMusicGeneration?.linkedTaskId,
    musicGeneration: activeMusicGeneration,
    onSongReady: handleSongReady,
  });

  useEffect(() => {
    if (designImage1 || designImage2) {
      hasSavedImage1.current = false;
      hasSavedImage2.current = false;
    }
  }, [designImage1, designImage2]);

  // Store music context when generation loads
  const hasLoggedGenerationStart = useRef(false);
  useEffect(() => {
    if (activeMusicGeneration && fanData) {
      setCurrentMusicContext({
        taskId: activeMusicGeneration.taskId,
        prompt: activeMusicGeneration.prompt,
        musicStyle: activeMusicGeneration.musicStyle,
        thumbnailUrl: fanData.full_image_url, // Use fan's image as thumbnail
      });

      if (!hasLoggedGenerationStart.current) {
        const eventProps = {
          companion_name: fanData.given_name || fanData.artist_alias || '',
          companion_slug: fanSlug || '',
          prompt_text: activeMusicGeneration.prompt || '',
          music_style: activeMusicGeneration.musicStyle || '',
          task_id: activeMusicGeneration.taskId || '',
        };

        analytics.logEvent('music_generation_start', eventProps);
        hasLoggedGenerationStart.current = true;
      }
    }
  }, [activeMusicGeneration, setCurrentMusicContext, fanData, fanSlug]);

  // Update selectedImageUrl and selectedImageId for designer session based on current images
  useEffect(() => {
    if (isDesignerSession) {
      // Set based on which image exists, prioritizing image1
      if (designImage1) {
        setSelectedImageUrl(designImage1);
        setSelectedImageId(1);
      } else if (designImage2) {
        setSelectedImageUrl(designImage2);
        setSelectedImageId(2);
      }
    }
  }, [
    isDesignerSession,
    designImage1,
    designImage2,
    setSelectedImageUrl,
    setSelectedImageId,
  ]);

  // Update the selected image when a new regenerated image is available
  useEffect(() => {
    if (regeneratedImageUrl && selectedImageId) {
      const currentImage = selectedImageId === 1 ? designImage1 : designImage2;
      // Only update if the URL actually changed to prevent unnecessary reloads
      if (currentImage !== regeneratedImageUrl) {
        if (selectedImageId === 1) {
          setDesignImage1(regeneratedImageUrl);
          hasSavedImage1.current = false;
        } else if (selectedImageId === 2) {
          setDesignImage2(regeneratedImageUrl);
          hasSavedImage2.current = false;
        }
      }
      // Clear the regenerated URL after processing
      setRegeneratedImageUrl(null);
      // set regenerating false
      setIsRegeneratingImage(false);
    }
  }, [
    regeneratedImageUrl,
    selectedImageId,
    designImage1,
    designImage2,
    setRegeneratedImageUrl,
    setIsRegeneratingImage,
  ]);

  useEffect(() => {
    if (designImage1 && !hasSavedImage1.current && userInput && fanData) {
      autoSaveImage({
        imageVersion: 1,
        imageUrl: designImage1,
        userInput,
        fanData,
        saveCreation,
        saveCreationAsync,
        savedImageCreationId1Ref: savedImageCreationId1,
        savedImageCreationId2Ref: savedImageCreationId2,
        onSaveComplete: (imageVersion: 1 | 2, savedId: string) => {
          if (imageVersion === 1) {
            setImage1SavedId(savedId);
          }
        },
      });
      hasSavedImage1.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designImage1, userInput, fanData]);

  useEffect(() => {
    if (designImage2 && !hasSavedImage2.current && userInput && fanData) {
      autoSaveImage({
        imageVersion: 2,
        imageUrl: designImage2,
        userInput,
        fanData,
        saveCreation,
        saveCreationAsync,
        savedImageCreationId1Ref: savedImageCreationId1,
        savedImageCreationId2Ref: savedImageCreationId2,
        onSaveComplete: (imageVersion: 1 | 2, savedId: string) => {
          if (imageVersion === 2) {
            setImage2SavedId(savedId);
          }
        },
      });
      hasSavedImage2.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designImage2, userInput, fanData]);

  useEffect(() => {
    if (
      song1Ready &&
      !hasSavedSong1.current &&
      activeMusicGeneration &&
      activeMusicGeneration.songUrl1 &&
      fanData
    ) {
      /*const isStreamUrl =
        activeMusicGeneration.songUrl1.includes('api.musicgpt.com') &&
        activeMusicGeneration.songUrl1.includes('/stream');*/

      autoSaveSong({
        songVersion: 1,
        activeMusicGeneration,
        fanData,
        saveCreation,
        saveCreationAsync,
        savedSongCreationId1Ref: savedSongCreationId1,
        savedSongCreationId2Ref: savedSongCreationId2,
        editedLyrics,
        editedTitle: editedTitles[1] ?? editedTitles[2] ?? null,
        onSaveComplete: async (songVersion: 1 | 2, savedId: string) => {
          if (songVersion === 1) {
            setSong1SavedId(savedId);
            // Store creation ID in cache (for webhook updates including lyrics-only webhooks)
            if (activeMusicGeneration?.taskId) {
              try {
                await updateSavedCreationId(activeMusicGeneration.taskId, {
                  songVersion: 1,
                  savedCreationId: savedId,
                });
              } catch (error) {
                console.error(
                  'Failed to update saved creation ID in cache:',
                  error,
                );
              }
            }
          }
        },
      });
      hasSavedSong1.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song1Ready, activeMusicGeneration, fanData]);

  useEffect(() => {
    if (
      song2Ready &&
      !hasSavedSong2.current &&
      activeMusicGeneration &&
      activeMusicGeneration.songUrl2 &&
      fanData &&
      !savedSongCreationId2.current
    ) {
      const isSongStream = isStreamUrl(activeMusicGeneration.songUrl2);

      console.log('[Generation] Auto-saving song 2', {
        song2Ready,
        songUrl2: activeMusicGeneration.songUrl2,
        isStreamUrl: isSongStream,
        taskId: activeMusicGeneration.taskId,
      });

      autoSaveSong({
        songVersion: 2,
        activeMusicGeneration,
        fanData,
        saveCreation,
        saveCreationAsync,
        savedSongCreationId1Ref: savedSongCreationId1,
        savedSongCreationId2Ref: savedSongCreationId2,
        editedLyrics,
        editedTitle: editedTitles[1] ?? editedTitles[2] ?? null,
        onSaveComplete: async (songVersion: 1 | 2, savedId: string) => {
          if (songVersion === 2) {
            console.log('[Generation] Song 2 saved, ID:', savedId);
            setSong2SavedId(savedId);
            // Store creation ID in cache (for webhook updates including lyrics-only webhooks)
            if (activeMusicGeneration?.taskId) {
              try {
                await updateSavedCreationId(activeMusicGeneration.taskId, {
                  songVersion: 2,
                  savedCreationId: savedId,
                });
                console.log(
                  '[Generation] Song 2 creation ID saved to cache',
                  savedId,
                );
              } catch (error) {
                console.error(
                  'Failed to update saved creation ID in cache:',
                  error,
                );
              }
            }
          }
        },
      });
      hasSavedSong2.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song2Ready, activeMusicGeneration, fanData, savedSongCreationId2]);

  // Patch cover art from initial generation when it arrives after auto-save
  useEffect(() => {
    const url = activeMusicGeneration?.thumbnailUrl1;
    if (
      url &&
      hasSavedSong1.current &&
      url !== lastPatchedCover1.current &&
      !musicCoverOverrides[1]
    ) {
      lastPatchedCover1.current = url;
      updateSongThumbnail(1, url);
    }
  }, [activeMusicGeneration?.thumbnailUrl1, musicCoverOverrides, updateSongThumbnail]);

  useEffect(() => {
    const url = activeMusicGeneration?.thumbnailUrl2;
    if (
      url &&
      hasSavedSong2.current &&
      url !== lastPatchedCover2.current &&
      !musicCoverOverrides[2]
    ) {
      lastPatchedCover2.current = url;
      updateSongThumbnail(2, url);
    }
  }, [activeMusicGeneration?.thumbnailUrl2, musicCoverOverrides, updateSongThumbnail]);

  const handleStopAvatar = () => {
    if (!isStationMode && isDataChannelOpen) {
      const data = {
        type: 'cancel',
        data: { sessionId },
      };
      sendMessage(data);
    }
  };

  useEffect(() => {
    const handleMicStateChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ isMuted: boolean }>;
      const { isMuted: micMuted } = customEvent.detail;
      setIsMuted(micMuted);
    };

    window.addEventListener('micStateChange', handleMicStateChange);
    return () => {
      window.removeEventListener('micStateChange', handleMicStateChange);
    };
  }, []);

  // reduce music volume when user unmutes (only if already above 20%)
  const prevIsMutedRef = useRef(isMuted);

  useEffect(() => {
    const wasMuted = prevIsMutedRef.current;
    const isNowUnmuted = wasMuted && !isMuted;

    if (isNowUnmuted && volume > 0.2) {
      dispatch(setVolume(0.2));
    }

    prevIsMutedRef.current = isMuted;
  }, [isMuted, volume, dispatch]);

  const hasAutoPlayedSong1Ref = useRef(false);

  useAvatarMessaging({
    isDataChannelOpen,
    activeMusicGeneration,
    song1Ready,
    song2Ready,
    userInput,
    fanData,
    designImage1,
    designImage2,
    sendMessageWithBridge,
    sendMessage,
    isPresentationMode,
    isWebpageMode,
    isBusinessPlanMode,
    isRegeneratingLyrics,
    isRegeneratingMusic,
  });

  // Handler for selecting image for regeneration
  const handleImageSelect = (imageVersion: 1 | 2) => {
    const imageUrl = imageVersion === 1 ? designImage1 : designImage2;
    if (!imageUrl) return;

    // Toggle selection
    if (selectedImageId === imageVersion) {
      setSelectedImageId(null);
      setSelectedImageUrl(null);
    } else {
      setSelectedImageId(imageVersion);
      setSelectedImageUrl(imageUrl);
    }
  };

  const {
    handleProduceSongs,
    countdownSeconds,
    showExtendedMessage,
    showProduceErrorModal,
    isContentModerationError,
    moderationReason,
    setShowProduceErrorModal,
    setIsContentModerationError,
    setModerationReason,
    hasClickedProduce,
  } = useGenerationProduce({
    activeMusicGeneration,
    isRegeneratingMusic,
    setIsRegeneratingMusic,
    editedLyrics,
    songsAreReady,
    isMusicGenerationSession,
    hasLyrics,
    hasSavedSong1,
    hasSavedSong2,
  });

  produceErrorModalSettersRef.current = {
    setShowProduceErrorModal,
    setIsContentModerationError,
    setModerationReason,
  };

  useEffect(() => {
    if (!showProduceErrorModal) return;
    window.dispatchEvent(new Event(DISCONNECT_AVATAR_PRESERVE_GENERATION_PAGE));
  }, [showProduceErrorModal]);

  // Handle content moderation failures from background lyrics/image generation
  // Moderation errors use 'failed' status with "Content moderation:" prefix in error field
  useEffect(() => {
    const errorMsg = activeMusicGeneration?.error || '';
    if (
      activeMusicGeneration?.status === 'failed' &&
      errorMsg.toLowerCase().startsWith('content moderation:')
    ) {
      const reason = errorMsg.replace(/^content moderation:\s*/i, '');
      setIsContentModerationError(true);
      setModerationReason(reason);
      setShowProduceErrorModal(true);
    }
  }, [activeMusicGeneration?.status, activeMusicGeneration?.error, setShowProduceErrorModal, setIsContentModerationError, setModerationReason]);

  // songs are ready, rest the producing flag
  useEffect(() => {
    if (activeMusicGeneration?.songUrl1 && activeMusicGeneration?.songUrl2) {
      setIsRegeneratingMusic(false);
    }
  }, [
    activeMusicGeneration?.songUrl1,
    activeMusicGeneration?.songUrl2,
    setIsRegeneratingMusic,
  ]);

  const handleCardSelect = useCallback(
    (songVersion: 1 | 2) => {
      if (!activeMusicGeneration) return;
      setSelectedSongId(songVersion);
    },
    [activeMusicGeneration, setSelectedSongId],
  );

  const handleCardRegenerate = useCallback(
    async (cardId: 'card1' | 'card2', prompt: string) => {
      const cleanedPrompt = prompt.trim();
      if (!cleanedPrompt) {
        return;
      }

      const cardVersion: 1 | 2 = cardId === 'card1' ? 1 : 2;

      // Log analytics for text-triggered artwork regeneration
      analytics.logEvent('music_generation_regenerate_artwork', {
        change_source: 'text',
        song_number: cardVersion,
      });

      if (isDesignerMode) {
        console.log('Setting selectedImageId to:', cardVersion);
        setSelectedImageId(cardVersion);
        // Also set the ref directly so it's available immediately
        selectedImageIdRef.current = cardVersion;
        const currentImage =
          cardVersion === 1 ? (designImage1 ?? null) : (designImage2 ?? null);
        setSelectedImageUrl(currentImage);
      } else {
        setSelectedSongId(cardVersion);
      }

      try {
        console.log(
          'Calling handleRegenerateCreation with cardVersion:',
          cardVersion,
        );
        await handleRegenerateCreation(
          {
            type: 'image',
            modification_request: cleanedPrompt,
          },
          (message) => {
            console.log('Regeneration update:', message);
          },
        );
      } catch (error) {
        console.error('Card regenerate failed:', error);
      }
    },
    [
      designImage1,
      designImage2,
      handleRegenerateCreation,
      isDesignerMode,
      setSelectedImageId,
      setSelectedImageUrl,
      setSelectedSongId,
    ],
  );

  const handleCardClick = (songVersion: 1 | 2) => {
    if (!activeMusicGeneration) return;
    console.log('handleCardClick');
    setSelectedSongId(songVersion);
    const isSongReady = songVersion === 1 ? song1Ready : song2Ready;
    if (!isSongReady || activeMusicGeneration.error) return;

    analytics.logEvent('music_generation_start_playback', {
      song_number: songVersion,
    });

    const playlist = buildGenerationPlaylist({
      songVersion,
      activeMusicGeneration,
      fanData,
      fanSlug,
      user: (user as OidcUser) || undefined,
      savedSongCreationId1,
      savedSongCreationId2,
    });

    if (!playlist.album.tracks.length) return;

    // Send stop-talking message to companion app when playing a song
    sendStopTalkingMessage();

    handleStopAvatar();

    window.dispatchEvent(
      new CustomEvent('audioControlSync', {
        detail: { volume: 0, muted: true },
      }),
    );

    window.dispatchEvent(
      new CustomEvent('micControlSync', {
        detail: { muted: true },
      }),
    );

    // Don't set active playlist in Station mode
    if (!isStationMode) {
      setActivePlaylist(playlist);
      setMusicPlaying(true);
      setActivePopup('musicplayer');
      setIsMinimized(true);
    }
  };

  const handleDesignerImageClick = useCallback(
    (imageVersion: 1 | 2) => {
      setSelectedImageId(imageVersion);
      const imageUrl = imageVersion === 1 ? designImage1 : designImage2;
      if (imageUrl) {
        setSelectedImageUrl(imageUrl);
      }
    },
    [designImage1, designImage2, setSelectedImageId, setSelectedImageUrl],
  );

  const handleRegenerateCover = useCallback(
    (imageVersion: 1 | 2) => {
      if (isDesignerSession) {
        // For designer session, set the selected image ID and URL
        setSelectedImageId(imageVersion);
        const imageUrl = imageVersion === 1 ? designImage1 : designImage2;
        if (imageUrl) {
          setSelectedImageUrl(imageUrl);
        }
      } else {
        // For music generation session, set the selected song ID
        setSelectedSongId(imageVersion);
      }
      setIsRegeneratingImage(true);
      handleRegenerateCreation(
        {
          type: 'image',
          modification_request: 'Regenerate cover image',
        },
        (msg: string) => {
          if (import.meta.env.DEV) {
            console.log('[Regenerate Cover]', msg);
          }
        },
      );
    },
    [
      isDesignerSession,
      designImage1,
      designImage2,
      handleRegenerateCreation,
      setSelectedImageId,
      setSelectedImageUrl,
      setSelectedSongId,
      setIsRegeneratingImage,
    ],
  );

  useStationAutoActions({
    isMusicGenerationSession,
    song1Ready,
    activeMusicGeneration,
    handleCardClick,
    song1SavedId,
  });

  const showImage1 = isDesignerMode
    ? designImage1
    : (musicCoverOverrides[1] ??
      activeMusicGeneration?.thumbnailUrl1 ??
      activeMusicGeneration?.musicGptCoverUrl ??
      null);
  const showImage2 = isDesignerMode
    ? designImage2
    : (musicCoverOverrides[2] ??
      activeMusicGeneration?.thumbnailUrl2 ??
      activeMusicGeneration?.musicGptCoverUrl ??
      null);
  const thumb1Ready = !!showImage1;
  const thumb2Ready = !!showImage2;

  const handlePause = useCallback(() => {
    setMusicPlaying(false);
  }, [setMusicPlaying]);

  const sharedEditedTitle = editedTitles[1] ?? editedTitles[2];

  const workflowCards = useWorkflowCards({
    isDesignerMode,
    isLoadingDesignImages,
    isRegeneratingMusic,
    isRegeneratingImage,
    designImage1,
    designImage2,
    song1Ready,
    song2Ready,
    selectedImageId,
    selectedSongId,
    isMusicPlaying: musicPlaying,
    isGenerationPlaylistActive:
      !!activePlaylist &&
      activePlaylist.album?.id === activeMusicGeneration?.taskId,
    showImage1,
    showImage2,
    thumb1Ready,
    thumb2Ready,
    activeMusicGeneration,
    sharedTrackTitle: sharedEditedTitle,
    fanData,
    handleCardClick,
    handleCardSelect,
    handleCardRegenerate,
    handleImageSelect,
    onPause: handlePause,
  });

  const isProducing = isDesignerMode
    ? isLoadingDesignImages
    : isRegeneratingMusic;
  const workflowSongsReady = isDesignerMode
    ? thumb1Ready && thumb2Ready
    : songsAreReady;

  const workflowSongVersion: 1 | 2 = selectedSongId === 2 ? 2 : 1;
  const workflowTitle =
    sharedEditedTitle ||
    (workflowSongVersion === 1
      ? activeMusicGeneration?.title1 || ''
      : activeMusicGeneration?.title2 || '');

  const handleCloseErrorModal = () => {
    setShowProduceErrorModal(false);
  };

  const handleGoHomeFromError = () => {
    navigate('/', { replace: true });
  };

  if (isStationMode && isPresentationMode) {
    return (
      <StationPresentationView
        presentationUrl={presentationUrl}
        isLoading={isLoadingPresentation}
      />
    );
  }

  // Use Station Business Plan UI for Station mode business plan sessions
  if (isStationMode && isBusinessPlanMode) {
    return (
      <StationBusinessPlanView
        businessPlanUrl={businessPlanUrl}
        isLoading={isLoadingBusinessPlan}
      />
    );
  }

  // Use Station Webpage UI for Station mode webpage sessions
  if (isStationMode && isWebpageMode) {
    return (
      <StationWebpageView
        webpageUrl={webpageUrl}
        isLoading={isLoadingWebpage}
      />
    );
  }

  // Use Station Designer UI for Station mode designer sessions
  if (isStationMode && isDesignerSession) {
    return (
      <StationDesignerUI
        designImage1={designImage1}
        designImage2={designImage2}
        isLoadingDesignImages={isLoadingDesignImages}
        userInput={userInput || ''}
        fanData={fanData}
        isRegeneratingImage={isRegeneratingImage}
        savedImageCreationId1={savedImageCreationId1}
        savedImageCreationId2={savedImageCreationId2}
        image1SavedId={image1SavedId}
        image2SavedId={image2SavedId}
        onRegenerateCover={handleRegenerateCover}
        onImageClick={handleDesignerImageClick}
      />
    );
  }

  if (
    isStationMode &&
    !isDesignerSession &&
    !isPresentationMode &&
    !isBusinessPlanMode &&
    !isWebpageMode
  ) {
    return (
      <>
        <StationMusicGenerationUI
          musicGeneration={activeMusicGeneration}
          fanData={fanData}
          fanSlug={fanSlug}
          editedLyrics={editedLyrics}
          onLyricsChange={(value) => setEditedLyrics(value)}
          onProduce={handleProduceSongs}
          song1Ready={song1Ready}
          song2Ready={song2Ready}
          song1SavedId={song1SavedId}
          song2SavedId={song2SavedId}
          isRegeneratingLyrics={isRegeneratingLyrics}
          isRegeneratingMusic={isRegeneratingMusic}
          isRegeneratingImage={isRegeneratingImage}
          onCardClick={handleCardClick}
          onRegenerateCover={handleRegenerateCover}
          onSongVersionChange={(v) => setSelectedSongId(v)}
          musicCoverOverrides={musicCoverOverrides}
        />
        <ProduceErrorModal
          visible={showProduceErrorModal}
          isContentModeration={isContentModerationError}
          moderationReason={moderationReason}
          onClose={handleCloseErrorModal}
          onGoHome={handleGoHomeFromError}
        />
      </>
    );
  }

  return (
    <>
      <WorkflowVisualization
        lyrics={editedLyrics ?? activeMusicGeneration?.lyrics ?? null}
        title={workflowTitle}
        isLoadingLyrics={!hasLyrics}
        isProducing={isProducing}
        songsReady={workflowSongsReady}
        hasClickedProduce={hasClickedProduce}
        onProduce={handleProduceSongs}
        onLyricsChange={(value) => setEditedLyrics(value)}
        onTitleChange={(value) =>
          handleGenerationTitleChange(workflowSongVersion, value)
        }
        mode={isDesignerMode ? 'image' : 'music'}
        cards={workflowCards}
        countdownSeconds={countdownSeconds}
        showExtendedMessage={showExtendedMessage}
      />
      <ProduceErrorModal
        visible={showProduceErrorModal}
        isContentModeration={isContentModerationError}
        moderationReason={moderationReason}
        onClose={handleCloseErrorModal}
        onGoHome={handleGoHomeFromError}
      />
    </>
  );
};
